<template>
  <v-menu
    v-model="showResults"
    transition="scale-transition"
    offset-y
    content-class="elevation-0"
    :close-on-content-click="false"
    :nudge-top="5"
  >
    <template v-slot:activator="{ args }">
      <v-text-field
        v-model="searchKeyword"
        @keyup="search"
        prepend-inner-icon="mdi-magnify"
        hide-details
        dense
        :placeholder="$t('searchApplication')"
        solo
        class="search-field"
        v-bind="args"
      ></v-text-field>
    </template>

    <v-sheet class="search-field-result pa-4">
      <v-skeleton-loader
        v-show="searching"
        loading
        type="paragraph"
      ></v-skeleton-loader>

      <div v-show="!searching && results">
        <v-list>
          <template v-for="item in results">
            <userItem
              v-if="item.type === 'user'"
              :item="item"
              :key="`search_user_${item.id}`"
              @click="clear"
            ></userItem>
          </template>
        </v-list>

        <div v-show="numResults > maxInitialResults">
          {{ numResults - maxInitialResults }} {{ $t('searchMoreMatches') }}
        </div>
      </div>

      <v-alert v-show="!searching && results.length === 0" type="warning" text>
        {{ $t('searchNoMatches') }}
      </v-alert>
    </v-sheet>
  </v-menu>
</template>

<script>
import { mapGetters } from 'vuex';

import userItem from './UserSearchResult.vue';

export default {
  data: () => ({
    showResults: false,
    searchKeyword: '',
    results: [],
    userSearch: false,
    fulltextSearch: false,
    debounceTimer: null,
    debounceInterval: 500,
    maxInitialResults: 5,
    numResults: -1
  }),

  computed: {
    ...mapGetters({
      employees: 'employees/getEmployees'
    }),

    searching() {
      return this.userSearch || this.fulltextSearch;
    }
  },

  methods: {
    getMatchingIndex(searchText) {
      if (
        searchText.toLowerCase().indexOf(this.searchKeyword.toLowerCase()) >= 0
      ) {
        return (this.searchKeyword.length * 100) / searchText.length;
      }

      return 0;
    },

    async search(event) {
      if (event.key.toLowerCase() === 'enter') {
        await this.searchFeedbacks();
      } else {
        await this.searchUsers();
      }
    },

    async searchUsers() {
      if (this.searchKeyword.length < 2) {
        this.showResults = false;
        return;
      }

      this.showResults = true;
      this.userSearch = true;
      this.results = [];
      this.numResults = -1;

      if (this.debounceTimer !== null) {
        clearTimeout(this.debounceTimer);
      }

      this.debounceTimer = setTimeout(() => {
        this.performSearch();
      }, this.debounceInterval);
    },

    async performSearch() {
      if (this.searchKeyword === '') {
        return;
      }

      const allResults = this.employees.filter(
        (x) =>
          (x.firstName + ' ' + x.lastName)
            .toLowerCase()
            .indexOf(this.searchKeyword.toLowerCase()) >= 0
      );

      this.numResults = allResults.length;
      const matchingData = allResults
        .map((x) => {
          return {
            type: 'user',
            id: x.id,
            firstName: x.firstName,
            lastName: x.lastName,
            nameIndex: this.getMatchingIndex(x.firstName + ' ' + x.lastName)
          };
        })
        .filter((x) => x.nameIndex > 0)
        .sort((a, b) => {
          return a.nameIndex < b.nameIndex;
        })
        .slice(0, this.maxInitialResults);

      this.results = [...this.results, ...matchingData];

      this.userSearch = false;
    },

    async searchFeedbacks() {
      this.fulltextSearch = true;

      const additionalResults = await this.searchFeedbackValues(
        this.searchKeyword
      );

      const matchingData = additionalResults.results.map((x) => {
        return {
          type: 'feedback',
          recipient: x.recipient,
          donor: x.donor,
          id: x.feedbackId,
          processType: x.processType,
          feedbackType: x.feedbackType
        };
      });

      this.results = [...this.results, ...matchingData];

      this.fulltextSearch = false;
    },

    clear() {
      this.showResults = false;
      this.searchKeyword = '';
    }
  },

  components: {
    userItem
  }
};
</script>
